import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { AlertTriangle, CheckCircle2, Circle, Clock3, ShieldAlert } from "lucide-react";
import { cn } from "../../lib/utils";

export type ClientListItem = {
  id: string;
  name: string;
  slug: string;
  disabledAt: string | null;
  lifecycle: "onboarding" | "live" | "offboarding" | null;
  segment: string | null;
  primaryContactName: string | null;
  committedTermEnd: string | null;
  onboardingStatus: string | null;
  operationId: string | null;
  operationState: string | null;
  operationUpdatedAt: string | null;
  billingPartyStatus: string | null;
  paddleEnvironment: string | null;
  subscriptionStatus: string | null;
  subscriptionDisplayStatus: string;
  nextBilledAt: string | null;
  userCount: number;
  unpaidInvoiceCount: number;
};

export type PublicOperation = {
  id: string;
  companyId: string;
  kind: string;
  state: string;
  workflowVersion: number;
  generation: number;
  priceBookVersion: number | null;
  chargeComponentVersion: number | null;
  tier: string | null;
  addonKeys: string[];
  extraCurrencies: string[];
  currency: string | null;
  purchaseOrderNumber: string | null;
  partyKind: "business" | "individual";
  partyExceptionRecorded: boolean;
  collectionVariant: "wire_invoice" | "invoice_with_checkout" | "automatic_checkout";
  checkoutEnabled: boolean;
  collectionExceptionRecorded: boolean;
  paddleCustomerId: string | null;
  paddleBusinessId: string | null;
  paddleAddressId: string | null;
  paddleTransactionId: string | null;
  paddleSubscriptionId: string | null;
  previewDigest: string | null;
  reviewDigest: string | null;
  lastErrorCode: string | null;
  preparedAt: string | null;
  issuedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PublicPreview = {
  operationGeneration: number;
  party: {
    customerId: string;
    businessId: string | null;
    addressId: string;
    kind: "business" | "individual";
  };
  package: {
    priceBookVersion: number;
    chargeComponentVersion: number | null;
    tier: string;
    addonKeys: string[];
    extraCurrencies: string[];
    currency: string;
    items: Array<{ componentKey: string; quantity: number; cadence: string }>;
  };
  collection: {
    variant: PublicOperation["collectionVariant"];
    collectionMode: "manual" | "automatic";
    checkoutEnabled: boolean;
    paymentTermsDays: number | null;
    exceptionReason: string | null;
  };
  purchaseOrderNumber: string | null;
  totals: {
    subtotalMinor: number;
    taxMinor: number;
    totalMinor: number;
    currency: string;
  };
};

export type TransactionRow = {
  id: string;
  paddleTransactionId: string;
  companyId: string | null;
  companyName: string | null;
  paddleSubscriptionId: string | null;
  invoiceNumber: string | null;
  kind: string | null;
  status: string;
  currency: string;
  subtotalMinor: number | null;
  taxMinor: number | null;
  totalMinor: number;
  dueAt: string | null;
  paymentTermsDays: number | null;
  collectionMode: string | null;
  checkoutEnabled: boolean | null;
  billedAt: string | null;
  paidAt: string | null;
  updatedAt: string;
  invoicePdfAvailable: boolean;
};

export function AdminPageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="ops-heading ops-admin-page-heading flex flex-wrap items-start justify-between gap-4">
      <div className="max-w-3xl">
        {eyebrow && <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">{eyebrow}</div>}
        <h2 className="text-xl font-semibold tracking-tight text-fg">{title}</h2>
        <p className="mt-1 text-sm leading-5 text-muted">{description}</p>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Panel({
  children,
  className,
  title,
  description,
  action,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <section className={cn("ops-panel ops-admin-panel rounded-xl border border-border bg-bg", className)}>
      {(title || description || action) && (
        <div className="ops-admin-panel-heading flex flex-wrap items-start justify-between gap-3 border-b border-border/70 px-4 py-3">
          <div>
            {title && <h3 className="ops-section-title text-sm font-semibold text-fg">{title}</h3>}
            {description && <p className="mt-0.5 text-xs leading-5 text-muted">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Metric({ label, value, detail, tone = "neutral" }: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: "neutral" | "good" | "warning" | "danger";
}) {
  return (
    <div className={cn(
      "ops-admin-metric rounded-xl border px-4 py-3",
      tone === "good" && "border-posGreen/30 bg-posGreen/5",
      tone === "warning" && "border-warnOrange/30 bg-warnOrange/5",
      tone === "danger" && "border-negRed/30 bg-negRed/5",
      tone === "neutral" && "border-border bg-bg"
    )}>
      <div className="ops-admin-metric-label text-xs font-medium text-muted">{label}</div>
      <div className="ops-admin-metric-value mt-1 text-xl font-semibold tabular-nums text-fg">{value}</div>
      {detail && <div className="mt-1 text-xs text-muted">{detail}</div>}
    </div>
  );
}

export function StateChip({ value, label }: { value: string | null | undefined; label?: string }) {
  const normalized = value ?? "none";
  const good = ["active", "paid", "complete", "completed", "ready", "live", "exempt"].includes(normalized);
  const danger = ["past_due", "overdue", "reversed", "suspended", "issue_uncertain", "reconciliation_required", "needs_reconciliation"].includes(normalized);
  const warning = ["required", "invoiced", "onboarding", "party_partial", "awaiting_payment", "issued_waiting_webhooks", "updating"].includes(normalized);
  const Icon = danger ? ShieldAlert : warning ? Clock3 : good ? CheckCircle2 : Circle;
  return (
    <span className={cn(
      "ops-admin-state inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap",
      good && "border-posGreen/30 bg-posGreen/10 text-posGreen",
      danger && "border-negRed/30 bg-negRed/10 text-negRed",
      warning && "border-warnOrange/30 bg-warnOrange/10 text-warnOrange",
      !good && !danger && !warning && "border-border bg-border/15 text-muted"
    )}>
      <Icon size={11} aria-hidden="true" />
      {label ?? humanize(normalized)}
    </span>
  );
}

export function Callout({ tone = "info", title, children }: {
  tone?: "info" | "warning" | "danger" | "success";
  title: string;
  children?: ReactNode;
}) {
  const Icon = tone === "success" ? CheckCircle2 : tone === "info" ? Circle : AlertTriangle;
  return (
    <div className={cn(
      "ops-admin-callout flex gap-2.5 rounded-lg border px-3 py-2.5 text-xs",
      tone === "info" && "border-accent/25 bg-accent/5",
      tone === "warning" && "border-warnOrange/30 bg-warnOrange/5",
      tone === "danger" && "border-negRed/30 bg-negRed/5",
      tone === "success" && "border-posGreen/30 bg-posGreen/5"
    )}>
      <Icon size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div>
        <div className="font-medium text-fg">{title}</div>
        {children && <div className="mt-0.5 leading-5 text-muted">{children}</div>}
      </div>
    </div>
  );
}

export function Button({ className, variant = "secondary", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "quiet";
}) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45",
        variant === "primary" && "ops-primary bg-accent text-white hover:bg-accent/90",
        variant === "secondary" && "border border-border bg-bg text-fg hover:border-accent/50",
        variant === "danger" && "border border-negRed/40 bg-negRed/5 text-negRed hover:bg-negRed/10",
        variant === "quiet" && "text-muted hover:bg-border/20 hover:text-fg",
        className
      )}
    />
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn("h-9 w-full min-w-0 rounded-md border border-border bg-bg px-2.5 text-sm text-fg outline-none placeholder:text-muted/70 focus:border-accent disabled:opacity-60", props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn("h-9 w-full min-w-0 rounded-md border border-border bg-bg px-2.5 text-sm text-fg outline-none focus:border-accent disabled:opacity-60", props.className)} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cn("min-h-24 w-full min-w-0 rounded-md border border-border bg-bg px-2.5 py-2 text-sm text-fg outline-none placeholder:text-muted/70 focus:border-accent disabled:opacity-60", props.className)} />;
}

export function FormField({ label, hint, required, children }: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="ops-admin-field block min-w-0 text-xs text-muted">
      <span className="font-medium text-fg">{label}{required && <span className="ml-0.5 text-negRed">*</span>}</span>
      {hint && <span className="ml-1 text-muted">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function KeyValue({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[minmax(8rem,0.8fr)_minmax(0,1.4fr)] gap-3 border-b border-border/50 py-2 last:border-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className={cn("min-w-0 break-words text-right text-xs text-fg", mono && "font-mono")}>{value ?? "—"}</dd>
    </div>
  );
}

export function formatMoney(amountMinor: number | null | undefined, currency = "GBP") {
  if (amountMinor == null) return "—";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amountMinor / 100);
  } catch {
    return `${currency} ${(amountMinor / 100).toFixed(2)}`;
  }
}

export function formatDate(value: string | null | undefined, withTime = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

export function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return humanize(error.message);
  return "Something went wrong. Refresh and try again.";
}
